<script lang="ts" setup>
import { useAudio } from "@/stores/audio";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { useGlobalWindow } from "@/stores/globalWindow";
import { useHome } from "@/stores/home";
import { useNotification } from "@/stores/notification";
import { StorageGet, StorageRemove, StorageSet } from "@/tools/localStorage";
import { useVh } from "@/use/common";
import "@vant/touch-emulator";
import { setToastDefaultOptions, showFailToast, showLoadingToast } from "vant";
import { computed, onBeforeMount, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouteLocationRaw, useRoute, useRouter } from "vue-router";
// import NSFWModalBuyItem from "./NSFW/Modal/NSFWModalBuyItem.vue";
import FixedRouterView from "@/components/fix/FixedRouterView.vue";
import { getLocaleById } from "@/locales";
import { useMate } from "@/stores/mate";
import ModalPackage from "./Modal/ModalPackage.vue";
import DesktopSprite from "@/features/desktop-sprite/DesktopSprite.vue";

// useCommonHead();
setToastDefaultOptions("loading", { forbidClick: true, duration: 0 });
// UI初始化
useVh();
// 内部状态
const route = useRoute();
const globalWindow = useGlobalWindow();
const notification = useNotification();
const homeStore = useHome();
const mateStore = useMate();
const userStore = useGlobalUser();
const globalModal = useGlobalModal();
const router = useRouter();
const { t, locale } = useI18n({ useScope: "global" });
// Lifecycle
onBeforeMount(() => {
  const audioStore = useAudio();
  audioStore.recoveryEnableAutoPlay();
});
watch(
  () => userStore.settingDisplayLanguageId,
  () => {
    locale.value = getLocaleById(userStore.settingDisplayLanguageId);
    if (document) {
      document
        .querySelector("html")
        ?.setAttribute("lang", locale.value as string);
    }
  }
);
const defaultNavList: {
  route?: RouteLocationRaw;
  icon: string;
  iconSelected: string;
  requireLogin?: boolean;
  indicator?: number;
  name?: string;
  hightlight?: boolean;
}[] = reactive([
  {
    route: { name: "dreamHome" },
    icon: "mdi--ferris-wheel",
    iconSelected: "mdi--ferris-wheel",
    name: t(`SidebarNavi.playground`),
    indicator: computed(() => notification.displayInteractionPoolSummary),
  },
  {
    route: { name: "treehole", query: { isRoot: true } },
    icon: "fluent-emoji-high-contrast--fish-cake-with-swirl",
    iconSelected: "fluent-emoji-high-contrast--fish-cake-with-swirl",
    name: t("Buttons.treehole"),
  },
  {
    route: { name: "discover" },
    icon: "mingcute--compass-line",
    iconSelected: " ph--compass-fill",
    name: t(`SidebarNavi.discover`),
  },
  {
    route: { name: "mine" },
    icon: "mingcute--user-4-line",
    iconSelected: "mingcute--user-4-fill",
    name: t(`Home.mine`),
    indicator: computed(() => notification.displayBadgeCount),
  },
]);
const navList = ref(defaultNavList);

watch(
  () => globalWindow.showLoading,
  (val) => {
    if (!val && route.hash) {
      if (route.hash.includes("successPayment")) {
        const loading = showLoadingToast({
          message: t(`Message.loading`),
          duration: 0,
        });
        const [_, received] = route.hash.split("&");
        console.log(_);
        if (received)
          StorageSet("paymentReceivedList", decodeURIComponent(received));
        setTimeout(() => {
          history.go(-3);
          setTimeout(loading.close, 2000);
        }, 500);
      } else if (route.hash.includes("cancelPayment")) {
        showFailToast(t(`Message.paymentCancelled`));
        history.go(-3);
      }
    }
  }
);
onMounted(() => {
  const { list, type, subscriptionType } =
    StorageGet("paymentReceivedList") || {};
  if (list && type) {
    if (subscriptionType) {
      userStore.checkPaymentSuccess(list, type, subscriptionType);
    } else {
      globalModal.openReceivedModal(list, type);
      StorageRemove("paymentReceivedList");
    }
  }

  const { mini_program_url } = route.query;
  if (mini_program_url) {
    globalModal.openMiniProgramModal(mini_program_url as string);
    router.replace(`/`);
  }
});

const clearCache = () => {
  caches.keys().then(function (names) {
    for (let name of names) caches.delete(name);
  });
  localStorage.clear();
  sessionStorage.clear();
  location.reload();
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then(function (registrations) {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  }
};
</script>
<template>
  <!-- 主容器: 通过padding绕过Bottom Navigator，高度 = vh - pb -->
  <div
    v-if="globalWindow.showLoading"
    class="h-full flex items-center justify-center flex-col gap-4 relative"
  >
    <LogoIcon class="w-1/2 max-w-[300px] text-base-content" />
    <div class="loading loading-ring text-primary loading-lg flex"></div>
    <button
      class="btn btn-xs md:btn-sm rounded absolute bottom-10 text-base-content/50"
      @click="clearCache"
    >
      {{ $t("SidebarNavi.clearCache") }}
    </button>
  </div>
  <div
    v-else
    id="main-view"
    :class="{
      'pt-with-nav':
        route.meta.showBottomNav &&
        !route.query.isSub &&
        userStore.isLogin &&
        !route.query.forceHideBottomNav,
    }"
    class="w-full h-full overflow-hidden justify-center items-center box-border relative"
  >
    <fixed-router-view v-slot="{ Component }">
      <keep-alive
        :include="[
          'MateHome',
          'DungeonsMine',
          'HomeInteractiveRelation',
          'MateMailListPage',
          'HomeDiscoverV2',
          'MissionHome',
          'MateWishHome',
          'MissionDispatchRank',
          'MateMemoryPage',
          'TreeholeIndex',
          'TreeholeConnect',
          'DriftCapsuleListPage',
          'AdoptionHome',
        ]"
      >
        <component :is="Component" />
      </keep-alive>
    </fixed-router-view>
    <!-- Bottom Navigator：fix position -->
    <div
      v-if="
        route.meta.showBottomNav &&
        !route.query.isSub &&
        !route.query.forceHideBottomNav
      "
      id="bottomNav"
      class="absolute bottom-0 h-[72px] flex justify-between items-center left-0 w-full z-[999] bg-base-200"
    >
      <template v-for="(item, i) in navList" :key="i">
        <RouterLink
          v-if="
            item.route &&
            (!item.requireLogin || (userStore.isLogin && mateStore.currentMate))
          "
          :to="item.route"
          class="flex-1 flex justify-center cursor-pointer relative hover:text-current"
        >
          <template #default="{ isExactActive }">
            <button
              class="relative"
              :class="{
                activeNav: isExactActive,
                defaultNav: !isExactActive,
              }"
            >
              <i
                :class="[
                  isExactActive ? item.iconSelected : item.icon,
                  'iconify pointer-events-none',
                ]"
              />

              <div v-if="item.indicator" class="indicator">
                <div
                  class="indicator-item badge badge-primary rounded-full badge-xs top-[-20px] right-[-5px] text-white p-0 w-5 h-5"
                >
                  <div>{{ item.indicator }}</div>
                </div>
              </div>

              <div v-if="item.name" class="text-xs">
                {{ item.name }}
              </div>
            </button>
          </template>
        </RouterLink>
        <div v-else-if="item.route" class="flex-1 flex justify-center relative">
          <button class="text-base-content/50">
            <i :class="['iconify', item.icon]" />
          </button>
        </div>
        <div v-else class="flex-1 flex justify-center cursor-pointer relative">
          <button
            :class="[
              'btn-circle btn btn-primary shadow-lg mt-[-40px]',
              homeStore.showAddModal && 'rotate-45',
            ]"
            @click="homeStore.showAddModal = !homeStore.showAddModal"
          >
            <i class="iconfont icon-a-"></i>
          </button>
        </div>
      </template>
    </div>

    <!-- 全局弹框 -->
    <ModalPackage />
    <!-- <button
      v-if="showBackToHome"
      class="absolute bottom-10 left-0 right-0 w-fit btn-primary rounded-full btn btn-sm m-auto"
      @click="router.push({ name: 'threads' })"
    >
      {{ $t("Texts.backHome") }}
    </button> -->
    <DesktopSprite />
    <TaskFloatingBubble />
  </div>
</template>
<style>
#app {
  position: relative;
  padding-top: env(safe-area-inset-top);
  width: 100vw;
  height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;
  @apply text-sm md:mx-auto bg-base-100 relative;
}

/** body: overflow-hidden保证非globalScroll下body不会整体滑动 */
:root body {
  @apply bg-base-100 overflow-hidden;
}

html,
body {
  overscroll-behavior: none;
}

.btm-nav {
  height: var(--bottom-nav-height);
}

.pt-with-nav {
  @apply pb-[var(--bottom-nav-height)];
}

.force-no-pt {
  padding-bottom: 0 !important;
}

.defaultNav {
  opacity: 0.4;
}
.highlight {
  @apply bg-primary rounded p-4 text-primary-content;
}
.highlight .indicator-item {
  top: -40px !important;
  right: -15px !important;
}
</style>
